import { weatherFrontStyle, weatherRadarSvg, weatherName, gameClockLabel, regionalWeatherSummary, regionalWeatherDetail, regionalForecastHtml } from "./weather-presentation.js";
import { initializeWeatherExplorer, updateWeatherExplorer, disposeWeatherExplorer, selectWeatherLocation, refreshWeatherExplorer, type RadarSelection } from "./weather-explorer.js";
import type { Layer, LayerGroup, Map as LeafletMap, Marker } from "leaflet";
import {
  GATEWAY_MAP_SIZE,
  isRegionalWeatherFront,
  observedWeatherFrontPosition,
  gatewayHudCoordinates,
  gatewayHudToMap,
  gatewayStoredMapPointToVulnona,
  gatewayZoneBoundaryWorldPoints,
  gatewayZones,
  gatewayWorldToMap,
  gatewayWorldToVulnonaMap,
  gatewayVulnonaMapToWorld,
  type GatewayZoneKind
} from "@isle/domain";

const MAP_SIZE = GATEWAY_MAP_SIZE;
export const GATEWAY_BASEMAP_URL =
  "https://vulnona.com/game/map/map/Gateway_v0.21.7/base/3.webp?2026.07.26";

type MapLayerName = "locations" | "sanctuaries" | "weather" | "outlook" | "migration" | "patrol" | "heatmap";

export interface ModelledWeatherOutlookRoute {
  id: "pit-eastbound-v1";
  label: "Pit stormfront outlook";
  startsAt: string;
  endsAt: string;
  provenance: "environment_director_model";
  authoritative: false;
  start: { x: number; y: number };
  finish: { x: number; y: number };
  radiusMetres: 350;
}

const MODELLED_OUTLOOK_FRESHNESS_MS = 180_000;
const PIT_STORMFRONT_OUTLOOK_ROUTE = {
  id: "pit-eastbound-v1",
  label: "Pit stormfront outlook",
  provenance: "environment_director_model",
  authoritative: false,
  start: { x: -367931.157, y: 279516.525 },
  finish: { x: -355931.157, y: 279516.525 },
  radiusMetres: 350
} as const;

interface RenatusLocationLabel {
  group: "water" | "outpost" | "landmark";
  x: number;
  y: number;
  label: string;
}

interface ActiveLocationLabel {
  marker: Marker;
  priority: number;
  sourceIndex: number;
}

// Pixel coordinates and names are published by the MIT-licensed Renatus map.
export const RENATUS_LOCATION_LABELS: readonly RenatusLocationLabel[] = [
  { group: "water", x: 3113, y: 820, label: "North Lake" },
  { group: "water", x: 1890, y: 1680, label: "Highlands Lake" },
  { group: "water", x: 3300, y: 1000, label: "North River" },
  { group: "water", x: 2285, y: 1165, label: "Water Access Lake" },
  { group: "water", x: 2615, y: 1435, label: "Water Access River" },
  { group: "water", x: 2765, y: 1795, label: "The Fork" },
  { group: "water", x: 3190, y: 1470, label: "Roaring Falls" },
  { group: "water", x: 3550, y: 1595, label: "East Lake" },
  { group: "water", x: 2740, y: 2290, label: "The Delta" },
  { group: "water", x: 1770, y: 2250, label: "Aviary Dam" },
  { group: "water", x: 2630, y: 2700, label: "The Estuary" },
  { group: "water", x: 2350, y: 2840, label: "Eastern Swamp" },
  { group: "water", x: 1925, y: 2940, label: "Western Swamp" },
  { group: "water", x: 1430, y: 2460, label: "South River" },
  { group: "water", x: 1380, y: 2820, label: "South Lake" },
  { group: "outpost", x: 2725, y: 640, label: "Site B14" },
  { group: "outpost", x: 2820, y: 960, label: "Site D15" },
  { group: "outpost", x: 2690, y: 1180, label: "Site E14" },
  { group: "outpost", x: 3140, y: 1160, label: "Site E17" },
  { group: "outpost", x: 2985, y: 1275, label: "Site F16" },
  { group: "outpost", x: 3540, y: 1310, label: "Site F19" },
  { group: "outpost", x: 3785, y: 1325, label: "Site F21" },
  { group: "outpost", x: 3645, y: 1545, label: "Site H20" },
  { group: "outpost", x: 3130, y: 2075, label: "Site K17" },
  { group: "outpost", x: 2870, y: 2140, label: "Site K15" },
  { group: "outpost", x: 2330, y: 1695, label: "Site I12" },
  { group: "outpost", x: 1975, y: 1780, label: "Site J10-1" },
  { group: "outpost", x: 1665, y: 2190, label: "Site K8" },
  { group: "outpost", x: 1500, y: 2065, label: "Site K7-1" },
  { group: "outpost", x: 1515, y: 2175, label: "Site K7-2" },
  { group: "outpost", x: 1340, y: 2055, label: "Site K6" },
  { group: "outpost", x: 1210, y: 1790, label: "Site I6" },
  { group: "outpost", x: 1800, y: 2710, label: "Site N9" },
  { group: "outpost", x: 2140, y: 2600, label: "Site N11" },
  { group: "outpost", x: 3690, y: 1095, label: "Eastern Dock" },
  { group: "outpost", x: 590, y: 3020, label: "Western Dock" },
  { group: "outpost", x: 3290, y: 835, label: "Northern Padock" },
  { group: "outpost", x: 3365, y: 1540, label: "Eastern Padock" },
  { group: "outpost", x: 855, y: 3045, label: "Southern Padock" },
  { group: "outpost", x: 1912, y: 2412, label: "Aviary" },
  { group: "outpost", x: 1965, y: 2200, label: "A.1" },
  { group: "outpost", x: 2130, y: 2370, label: "A.2" },
  { group: "outpost", x: 2070, y: 2565, label: "A.3" },
  { group: "outpost", x: 1740, y: 2275, label: "A.6" },
  { group: "landmark", x: 2950, y: 1710, label: "Split Rock" },
  { group: "landmark", x: 3510, y: 1890, label: "Tidal Pools" },
  { group: "landmark", x: 1540, y: 1820, label: "Kissing Rocks" },
  { group: "landmark", x: 1910, y: 1870, label: "Highlands Underpass" },
  { group: "landmark", x: 1550, y: 1650, label: "Highlands Valley" },
  { group: "landmark", x: 1150, y: 2740, label: "Snakehead Lagoon" },
  { group: "landmark", x: 1585, y: 2235, label: "The Gorge" },
  { group: "landmark", x: 1200, y: 965, label: "Key Hole" },
  { group: "landmark", x: 2930, y: 1135, label: "Caldera" },
  { group: "landmark", x: 2485, y: 825, label: "Stone Archway" },
  { group: "landmark", x: 1655, y: 2940, label: "Swamp Valley" },
  { group: "landmark", x: 1035, y: 2465, label: "The Sandbars" },
  { group: "landmark", x: 2140, y: 3430, label: "Razor Rocks" },
  { group: "landmark", x: 3470, y: 280, label: "Northern Point" },
  { group: "landmark", x: 1250, y: 3380, label: "Southern Point" },
  { group: "landmark", x: 2620, y: 2410, label: "Seaview Plateau" }
];

export interface LiveMapPoint {
  name: string;
  species: string;
  latitude: number;
  longitude: number;
  kind: "self" | "member";
}

export interface HeatmapPoint {
  latitude: number;
  longitude: number;
  density: "low" | "medium" | "high";
}

export interface LiveMapPayload {
  points: LiveMapPoint[];
  heatmap: HeatmapPoint[];
  weather: {
    condition: string;
    intensity: number;
    cloudCoverage: number | null;
    rainIntensity: number | null;
    fogIntensity: number | null;
    windIntensity: number | null;
    lightningIntensity: number | null;
    timeOfDay: number | null;
    observedAt: string;
    evidenceState: "fresh" | "unavailable";
    controllerEvidenceState: "fresh" | "unavailable";
    livingWorld: {
      fronts?: Array<Omit<NonNullable<LiveMapPayload["weather"]["livingWorld"]>, "fronts">>;
      available: boolean;
      effect: "none" | "snow_frost_preview" | "aurora_2d" | "stormfront" | "snowfront" | "dustfront" | "marshveil";
      phase: string;
      region: "none" | "whole_island" | "targeted_clients" | "the_pit" | "highlands" | "east_swamp" | "west_swamp" | "river_delta" | "east_lake" | "west_coast" | "south_lake";
      regional?: { preset?: string; alpha?: number; ownership: string; mode: "automatic" | "manual" | "disabled"; next?: { preset: string; region: string; at: number }; upcoming?: Array<{ preset: string; region: string; at: number }>; aurora?: { chancePercent: number; night: number; status: "selected" | "not_selected" | "attempted" | "unavailable" } };
      observedAt: string;
      endsAt: string | null;
      route?: {
        id: string;
        start: { x: number; y: number };
        finish: { x: number; y: number };
        radiusMetres: number;
        position?: { x: number; y: number; observedAt: number };
      };
    } | null;
    director: {
      season: "dry" | "intermediate" | "wet" | "winter";
      mode: "disabled" | "forecast_only" | "edge_armed" | "production_armed";
      source: "environment_director_model";
      authoritative: false;
      generatedAt: string | null;
      decisionReason: string | null;
      cadence?: {
        revision: "environment-director-cadence-r1";
        precipitationFraction: number;
        stormFraction: number;
        longestCalmRunMinutes: number;
        maximumConsecutivePrecipitationWindows: number;
        nightVisibilityProtectedWindows: number;
      } | null;
      forecast: Array<{
        id: string;
        startsAt: string;
        endsAt: string;
        condition: string;
        intensity: number;
        reviewedOpportunity: { key: string; label: string; maturity: string } | null;
      }>;
      updatedAt: string;
    } | null;
  };
}

interface SavedMapView {
  center: [number, number];
  zoom: number;
}

let activeMap: LeafletMap | undefined;
let activeLeaflet: typeof import("leaflet") | undefined;
let activeLayers = new Map<MapLayerName, Layer>();
let activePlayerLayer: LayerGroup | undefined;
let selfPosition: [number, number] | undefined;
let activeOverviewZoom = -11;
let savedView: SavedMapView | undefined;
let initializationSequence = 0;
let activeLocationLabels: ActiveLocationLabel[] = [];
let locationLayoutFrame: number | undefined;
let calibrationMode = false;
let activeSelfPoint: LiveMapPoint | undefined;
let activeWeather: LiveMapPayload["weather"] | undefined;
let radarExplorerLayer: LayerGroup | undefined;

const CALIBRATION_STORAGE_KEY = "sunny-isle:gateway-calibration-samples";

interface MapCalibrationSample {
  capturedAt: string;
  name: string;
  species: string;
  worldX: number;
  worldY: number;
  targetMapX: number;
  targetMapY: number;
  currentMapX: number;
  currentMapY: number;
}

export function initializeLiveMap(): void {
  const container = document.querySelector<HTMLElement>("#liveMapCanvas");
  const payloadElement = document.querySelector<HTMLScriptElement>("#liveMapPayload");
  if (!container || !payloadElement) return;

  const payload = parsePayload(payloadElement.textContent ?? "");
  if (!payload) {
    container.innerHTML = '<p class="map-runtime-error">Live map data could not be read.</p>';
    return;
  }

  activeSelfPoint = payload.points.find((point) => point.kind === "self");

  const sequence = ++initializationSequence;
  void mountLiveMap(container, payload, sequence);
}

export function weatherWithFreshEvidence(weather: LiveMapPayload["weather"], now = Date.now()): LiveMapPayload["weather"] {
  const globalTime = Date.parse(weather.observedAt);
  const controllerTime = weather.livingWorld ? Date.parse(weather.livingWorld.observedAt) : 0;
  const globalExpired = weather.evidenceState === "fresh" && (!Number.isFinite(globalTime) || globalTime > now || now - globalTime > 60_000);
  const controllerExpired = weather.controllerEvidenceState === "fresh" && (!Number.isFinite(controllerTime) || controllerTime > now || now - controllerTime > 30_000);
  if (!globalExpired && !controllerExpired) return weather;
  return { ...weather,
    ...(globalExpired ? { evidenceState: "unavailable" } : {}),
    ...(controllerExpired || globalExpired ? { controllerEvidenceState: "unavailable", livingWorld: weather.livingWorld?.regional
      ? { ...weather.livingWorld, available: false, effect: "none", region: "none", route: undefined, endsAt: null } : null } : {}) };
}

export function expireLiveWeatherEvidence(now = Date.now()): void {
  if (!activeMap || !activeLeaflet || !activeWeather) return;
  const expired = weatherWithFreshEvidence(activeWeather, now);
  if (expired === activeWeather) return;
  replaceWeatherLayer(activeLeaflet, activeMap, expired);
  replaceEnvironmentOutlookLayer(activeLeaflet, activeMap, expired);
  updateWeatherExplorer(expired, selfWeatherPoint());
}

export function updateLiveMap(payload: LiveMapPayload): boolean {
  if (!activeMap || !activeLeaflet) return false;

  activeSelfPoint = payload.points.find((point) => point.kind === "self");
  replaceWeatherLayer(activeLeaflet, activeMap, payload.weather);
  replaceEnvironmentOutlookLayer(activeLeaflet, activeMap, payload.weather);
  replaceHeatmapLayer(activeLeaflet, activeMap, payload.heatmap);
  replacePlayerMarkers(activeLeaflet, activeMap, payload.points);
  updateWeatherExplorer(payload.weather, selfWeatherPoint());
  scheduleLocationLabelLayout(activeMap);
  return true;
}

export function disposeLiveMap(): void {
  disposeWeatherExplorer();
  radarExplorerLayer = undefined;
  initializationSequence += 1;
  if (activeMap) {
    activeMap.stop();
    const center = activeMap.getCenter();
    savedView = { center: [center.lat, center.lng], zoom: activeMap.getZoom() };
    activeMap.off();
    activeMap.remove();
  }
  activeMap = undefined;
  activeLeaflet = undefined;
  activePlayerLayer = undefined;
  activeLayers = new Map();
  activeLocationLabels = [];
  if (locationLayoutFrame !== undefined) window.cancelAnimationFrame(locationLayoutFrame);
  locationLayoutFrame = undefined;
  selfPosition = undefined;
  activeSelfPoint = undefined;
}

export function toggleLiveMapCalibration(): boolean {
  calibrationMode = !calibrationMode;
  syncCalibrationUi();
  return calibrationMode;
}

export function clearLiveMapCalibrationSamples(): void {
  try {
    window.localStorage.removeItem(CALIBRATION_STORAGE_KEY);
  } catch {
    // Calibration remains usable when browser storage is unavailable.
  }
  renderCalibrationSamples([]);
}

export async function copyLiveMapCalibrationSamples(): Promise<number> {
  const samples = readCalibrationSamples();
  if (samples.length > 0) await navigator.clipboard.writeText(JSON.stringify(samples, null, 2));
  return samples.length;
}

export function setLiveMapLayerVisibility(layerName: string, enabled: boolean): void {
  if (!isMapLayerName(layerName) || !activeMap) return;
  const layer = activeLayers.get(layerName);
  if (!layer) return;
  if (enabled && !activeMap.hasLayer(layer)) layer.addTo(activeMap);
  if (!enabled && activeMap.hasLayer(layer)) activeMap.removeLayer(layer);
  if (layerName === "locations" && enabled) scheduleLocationLabelLayout(activeMap);
  if (layerName === "heatmap") syncHeatmapLegend(enabled);
  if (layerName === "weather") { syncWeatherLensVisibility(enabled); refreshWeatherExplorer(); }
}

export function setLiveMapMonochrome(enabled: boolean): void {
  document.querySelector<HTMLElement>("#liveMapCanvas")?.classList.toggle("is-monochrome", enabled);
}

export function focusLiveWeather(): void {
  if (!activeMap || !activeWeather || !activeLeaflet) return;
  const position = liveWeatherFrontPosition(activeWeather);
  const route = activeWeather.livingWorld?.route;
  if (!position || !route) return;
  const radius = route.radiusMetres * 155;
  const sw = gatewayWorldToVulnonaMap(position.y + radius, position.x - radius);
  const ne = gatewayWorldToVulnonaMap(position.y - radius, position.x + radius);
  activeMap.fitBounds([[sw.lat, sw.lng], [ne.lat, ne.lng]], { padding: [24, 24], maxZoom: activeOverviewZoom + 3,
    animate: !window.matchMedia("(prefers-reduced-motion: reduce)").matches });
  document.querySelector<HTMLElement>("#liveMapCanvas")?.scrollIntoView({ block: "center",
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
}

export function recenterLiveMap(): void {
  if (!activeMap) return;
  if (selfPosition) activeMap.setView(selfPosition, Math.max(activeMap.getZoom(), activeOverviewZoom + 2), { animate: true });
  else activeMap.fitBounds(gatewayBounds(), { animate: true, padding: [12, 12] });
}

export { gatewayHudCoordinates, gatewayHudToMap, gatewayWorldToMap };

async function mountLiveMap(container: HTMLElement, payload: LiveMapPayload, sequence: number): Promise<void> {
  try {
    const leafletModule = await import("leaflet");
    const L = (leafletModule as { default?: typeof import("leaflet") }).default ?? leafletModule;
    if (sequence !== initializationSequence || !document.body.contains(container)) return;

    const bounds = L.latLngBounds(gatewayBounds());
    const map = L.map(container, {
      crs: L.CRS.Simple,
      minZoom: -14,
      maxZoom: -3,
      zoomSnap: 0.25,
      zoomDelta: 0.25,
      wheelDebounceTime: 25,
      wheelPxPerZoomLevel: 120,
      zoomAnimation: true,
      fadeAnimation: true,
      markerZoomAnimation: true,
      maxBounds: bounds.pad(0.18),
      maxBoundsViscosity: 0.8,
      attributionControl: false
    });
    activeMap = map;
    activeLeaflet = L;
    activeOverviewZoom = map.getBoundsZoom(bounds, false, L.point(24, 24));
    map.setMinZoom(activeOverviewZoom);
    map.setMaxZoom(activeOverviewZoom + 7);

    L.imageOverlay(GATEWAY_BASEMAP_URL, bounds, {
      interactive: false,
      className: "map-basemap-image"
    }).addTo(map);
    registerGatewayZoneLayer(L, map, "sanctuaries", "sanctuary");
    registerWeatherLayer(L, map, payload.weather);
    registerEnvironmentOutlookLayer(L, map, payload.weather);
    registerGatewayZoneLayer(L, map, "migration", "migration");
    registerGatewayZoneLayer(L, map, "patrol", "patrol");
    registerHeatmapLayer(L, map, payload.heatmap);
    registerPlayerMarkers(L, map, payload.points);
    const locationLayer = L.layerGroup();
    activeLayers.set("locations", locationLayer);
    registerLocationLabels(L, map, locationLayer);
    if (layerEnabled("locations", true)) locationLayer.addTo(map);

    if (savedView) {
      const zoom = Math.max(activeOverviewZoom, Math.min(activeOverviewZoom + 7, savedView.zoom));
      map.setView(savedView.center, zoom, { animate: false });
    } else {
      map.fitBounds(bounds, { animate: false, padding: [24, 24] });
    }

    map.on("zoomend moveend resize", () => scheduleLocationLabelLayout(map));
    map.whenReady(() => {
      map.invalidateSize();
      scheduleLocationLabelLayout(map);
    });
    map.on("click", (event: import("leaflet").LeafletMouseEvent) => {
      if (!calibrationMode) {
        const point = gatewayVulnonaMapToWorld(event.latlng.lng, MAP_SIZE - event.latlng.lat);
        selectWeatherLocation({ x: point.worldX, y: point.worldY });
        return;
      }
      captureCalibrationSample(event.latlng.lat, event.latlng.lng);
    });
    initializeWeatherExplorer(payload.weather, drawRadarExplorer, selfWeatherPoint());
    syncCalibrationUi();
    syncHeatmapLegend(layerEnabled("heatmap", false));
  } catch (error) {
    console.error("Live map initialization failed.", error);
    if (sequence === initializationSequence && document.body.contains(container)) {
      container.innerHTML = '<p class="map-runtime-error">The interactive map could not be loaded. Refresh this page to try again.</p>';
    }
  }
}

function captureCalibrationSample(targetLat: number, targetLng: number): void {
  const point = activeSelfPoint;
  if (!point) {
    const output = document.querySelector<HTMLElement>("[data-map-calibration-output]");
    if (output) output.textContent = "A fresh live player position is required before capturing a sample.";
    return;
  }

  const current = gatewayWorldToVulnonaMap(point.latitude, point.longitude);
  const sample: MapCalibrationSample = {
    capturedAt: new Date().toISOString(),
    name: point.name,
    species: point.species,
    worldX: roundCalibrationValue(point.longitude),
    worldY: roundCalibrationValue(point.latitude),
    targetMapX: roundCalibrationValue(targetLng),
    targetMapY: roundCalibrationValue(MAP_SIZE - targetLat),
    currentMapX: roundCalibrationValue(current.lng),
    currentMapY: roundCalibrationValue(MAP_SIZE - current.lat)
  };
  const samples = [...readCalibrationSamples(), sample].slice(-12);
  try {
    window.localStorage.setItem(CALIBRATION_STORAGE_KEY, JSON.stringify(samples));
  } catch {
    // The current sample is still shown even if private browsing blocks storage.
  }
  renderCalibrationSamples(samples);
}

function syncCalibrationUi(): void {
  const container = document.querySelector<HTMLElement>("#liveMapCanvas");
  const panel = document.querySelector<HTMLElement>("[data-map-calibration-panel]");
  const toggle = document.querySelector<HTMLButtonElement>("[data-map-calibration-toggle]");
  container?.classList.toggle("is-calibrating", calibrationMode);
  if (panel) panel.hidden = !calibrationMode;
  if (toggle) toggle.textContent = calibrationMode ? "Stop calibration" : "Calibrate position";
  if (calibrationMode) renderCalibrationSamples(readCalibrationSamples());
}

function renderCalibrationSamples(samples: MapCalibrationSample[]): void {
  const output = document.querySelector<HTMLElement>("[data-map-calibration-output]");
  if (!output) return;
  output.textContent = samples.length > 0
    ? `${samples.length} sample${samples.length === 1 ? "" : "s"} captured\n${JSON.stringify(samples, null, 2)}`
    : "No samples captured yet.";
}

function readCalibrationSamples(): MapCalibrationSample[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CALIBRATION_STORAGE_KEY) ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCalibrationSample).slice(-12);
  } catch {
    return [];
  }
}

function isCalibrationSample(value: unknown): value is MapCalibrationSample {
  if (!value || typeof value !== "object") return false;
  const sample = value as Partial<MapCalibrationSample>;
  return [sample.worldX, sample.worldY, sample.targetMapX, sample.targetMapY, sample.currentMapX, sample.currentMapY]
    .every((entry) => typeof entry === "number" && Number.isFinite(entry));
}

function roundCalibrationValue(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function registerGatewayZoneLayer(
  L: typeof import("leaflet"),
  map: LeafletMap,
  name: "sanctuaries" | "migration" | "patrol",
  kind: GatewayZoneKind
): void {
  const style = gatewayZoneStyle(kind);
  const layer = L.layerGroup(gatewayZones(kind).flatMap((zone) => {
    const points = gatewayZoneBoundaryWorldPoints(zone).map((point) => {
      const mapped = gatewayWorldToVulnonaMap(point.worldY, point.worldX);
      return [mapped.lat, mapped.lng] as [number, number];
    });
    const zonePolygon = L.polygon(points, {
      interactive: false,
      className: `gateway-zone gateway-zone-${kind}`,
      color: style.stroke,
      fillColor: style.fill,
      fillOpacity: style.fillOpacity,
      opacity: style.opacity,
      weight: style.weight,
      lineCap: "round",
      lineJoin: "round",
      ...(style.dashArray ? { dashArray: style.dashArray } : {})
    });
    if (kind === "sanctuary") return [zonePolygon];

    const contrastCasing = L.polygon(points, {
      interactive: false,
      className: `gateway-zone-casing gateway-zone-casing-${kind}`,
      color: kind === "patrol" ? "#160d1c" : "#07100d",
      fill: false,
      opacity: 0.94,
      weight: kind === "patrol" ? 4.8 : 4.4,
      lineCap: "round",
      lineJoin: "round",
      ...(style.dashArray ? { dashArray: style.dashArray } : {})
    });
    return [contrastCasing, zonePolygon];
  }));
  activeLayers.set(name, layer);
  if (layerEnabled(name, kind === "sanctuary")) layer.addTo(map);
}

export function weatherLensStyle(weather: LiveMapPayload["weather"]): { color: string; opacity: number; kind: "clear" | "cloud" | "rain" | "fog" | "storm" } {
  if (weather.evidenceState !== "fresh") return { color: "#000000", opacity: 0, kind: "clear" };
  const condition = weather.condition.trim().toLowerCase();
  const intensity = Math.max(0, Math.min(100, weather.intensity)) / 100;
  if (!condition || condition === "unknown" || condition === "unavailable") return { color: "#000000", opacity: 0, kind: "clear" };
  if (condition.includes("storm")) return { color: "#354552", opacity: .055 + intensity * .13, kind: "storm" };
  if (condition.includes("rain") || (weather.rainIntensity ?? 0) > 4) return { color: "#346776", opacity: .04 + intensity * .1, kind: "rain" };
  if (condition.includes("fog")) return { color: "#a7b5ae", opacity: .035 + intensity * .08, kind: "fog" };
  if (condition.includes("cloud") || (weather.cloudCoverage ?? 0) > 45) return { color: "#6d7c77", opacity: .025 + intensity * .06, kind: "cloud" };
  return { color: "#c8a963", opacity: .008 + intensity * .018, kind: "clear" };
}

function registerWeatherLayer(L: typeof import("leaflet"), map: LeafletMap, weather: LiveMapPayload["weather"]): void {
  const labels = map.getPane("weatherLabels") ?? map.createPane("weatherLabels");
  labels.style.zIndex = "660";
  labels.style.pointerEvents = "none";
  activeWeather = weather;
  const style = weatherLensStyle(weather);
  const layer = L.layerGroup();
  if (style.opacity > 0) {
    L.rectangle(gatewayBounds(), {
      interactive: false,
      stroke: false,
      fillColor: style.color,
      fillOpacity: style.opacity,
      className: `map-weather-lens map-weather-lens-${style.kind}`
    }).addTo(layer);
  }
  registerLivingWorldRoute(L, layer, weather);
  activeLayers.set("weather", layer);
  const container = map.getContainer();
  container.dataset.weatherObservedKind = style.opacity > 0 ? style.kind : "unavailable";
  container.style.setProperty("--weather-intensity", String(Math.max(0, Math.min(100, weather.intensity)) / 100));
  const enabled = layerEnabled("weather", weather.evidenceState === "fresh");
  if (enabled) layer.addTo(map);
  syncWeatherLensVisibility(enabled);
}

export function modelledWeatherOutlookRoute(
  weather: LiveMapPayload["weather"],
  now = Date.now()
): ModelledWeatherOutlookRoute | null {
  if (weather.livingWorld?.regional) return null;
  const director = weather.director;
  if (!director || director.mode === "disabled" || director.authoritative !== false) return null;
  const updatedAt = new Date(director.updatedAt).getTime();
  if (!Number.isFinite(updatedAt) || now - updatedAt < 0 || now - updatedAt > MODELLED_OUTLOOK_FRESHNESS_MS) return null;
  const window = director.forecast.find((candidate) =>
    new Date(candidate.endsAt).getTime() > now
    && candidate.reviewedOpportunity?.key === "pit_moving_stormfront"
    && candidate.reviewedOpportunity.maturity === "edge_accepted");
  if (!window) return null;
  return {
    ...PIT_STORMFRONT_OUTLOOK_ROUTE,
    startsAt: window.startsAt,
    endsAt: window.endsAt
  };
}

function registerEnvironmentOutlookLayer(
  L: typeof import("leaflet"),
  map: LeafletMap,
  weather: LiveMapPayload["weather"]
): void {
  const layer = L.layerGroup();
  const outlook = modelledWeatherOutlookRoute(weather);
  if (outlook) {
    const start = gatewayWorldToVulnonaMap(outlook.start.y, outlook.start.x);
    const finish = gatewayWorldToVulnonaMap(outlook.finish.y, outlook.finish.x);
    L.polyline([[start.lat, start.lng], [finish.lat, finish.lng]], {
      interactive: false,
      className: "environment-outlook-route",
      color: "#d6a84e",
      opacity: .62,
      weight: 2.4,
      dashArray: "3 10",
      lineCap: "round"
    }).addTo(layer);
    for (const point of [start, finish]) {
      L.circleMarker(point, {
        interactive: false,
        className: "environment-outlook-node",
        color: "#f4d898",
        fillColor: "#a96d20",
        fillOpacity: .28,
        opacity: .78,
        radius: 6,
        weight: 1.5
      }).addTo(layer);
    }
  }
  activeLayers.set("outlook", layer);
  if (layerEnabled("outlook", Boolean(outlook))) layer.addTo(map);
}

function selfWeatherPoint(): { x: number; y: number } | undefined {
  return activeSelfPoint && activeSelfPoint.latitude !== null && activeSelfPoint.longitude !== null
    ? { x: activeSelfPoint.longitude, y: activeSelfPoint.latitude } : undefined;
}

function drawRadarExplorer(selection: RadarSelection, selected?: { x: number; y: number }): void {
  const L = activeLeaflet, map = activeMap;
  if (!L || !map) return;
  if (radarExplorerLayer) map.removeLayer(radarExplorerLayer);
  radarExplorerLayer = L.layerGroup().addTo(map);
  const live = activeLayers.get("weather");
  const enabled = document.querySelector<HTMLInputElement>('[data-map-layer-toggle="weather"]')?.checked !== false;
  if (live) {
    if (selection.mode === "live" && enabled) { if (!map.hasLayer(live)) live.addTo(map); }
    else if (map.hasLayer(live)) map.removeLayer(live);
  }
  // History/forecast only replace environmental geometry. Private player markers remain live.
  if (enabled) for (const front of selection.fronts) {
    const p = front.route.position, radius = front.route.radiusMetres * 100;
    const sw = gatewayWorldToVulnonaMap(p.y + radius, p.x - radius), ne = gatewayWorldToVulnonaMap(p.y - radius, p.x + radius);
    const style = weatherFrontStyle(front.preset);
    L.imageOverlay('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(weatherRadarSvg(front.preset)),
      [[sw.lat, sw.lng], [ne.lat, ne.lng]], { opacity: selection.mode === "forecast" ? .3 : .75 * front.alpha, interactive: false }).addTo(radarExplorerLayer);
    const ring = Array.from({length:49}, (_, i) => { const a = i/48*Math.PI*2;
      const mapped = gatewayWorldToVulnonaMap(p.y + Math.sin(a)*radius, p.x + Math.cos(a)*radius);
      return [mapped.lat,mapped.lng] as [number,number]; });
    L.polyline(ring, { color: style.color, weight: 2, dashArray: selection.mode === "forecast" ? "7 8" : undefined, opacity: .7, interactive: false }).addTo(radarExplorerLayer);
    const center = gatewayWorldToVulnonaMap(p.y,p.x);
    L.marker([center.lat,center.lng], { interactive:false, pane:"weatherLabels", icon:L.divIcon({ className:"weather-explorer-map-label",
      html:`${selection.mode === "forecast" ? "ESTIMATED" : "RECORDED"}<strong>${escapeHtml(weatherName(front.preset))}</strong>`, iconSize:[140,42], iconAnchor:[70,21] }) }).addTo(radarExplorerLayer);
  }
  if (enabled && selection.mode !== "forecast") {
    const tracks = new Map<string, { color:string; points:[number,number][]; lastAt:number }>();
    for (const frame of selection.trails) for (const front of frame.fronts) {
      let track = tracks.get(front.id);
      if (!track) { track = { color:weatherFrontStyle(front.preset).color, points:[], lastAt:frame.at }; tracks.set(front.id,track); }
      if (frame.at-track.lastAt > 90_000) track.points = [];
      const p = gatewayWorldToVulnonaMap(front.route.position.y,front.route.position.x);
      track.points.push([p.lat,p.lng]); track.lastAt = frame.at;
    }
    for (const track of tracks.values()) if (track.points.length > 1) L.polyline(track.points, { color:track.color, opacity:.4, weight:4, interactive:false }).addTo(radarExplorerLayer);
  }
  if (selected) {
    const p = gatewayWorldToVulnonaMap(selected.y,selected.x);
    L.circleMarker([p.lat,p.lng], { radius:7,color:"#fff2b2",weight:2,fillColor:"#0c201c",fillOpacity:1,interactive:false }).addTo(radarExplorerLayer);
  }
}

function registerLivingWorldRoute(
  L: typeof import("leaflet"), layer: LayerGroup, weather: LiveMapPayload["weather"]
): void {
  if (weather.controllerEvidenceState !== "fresh" || !weather.livingWorld?.available) return;
  const fronts = weather.livingWorld.fronts;
  if (fronts) {
    for (const front of fronts) registerSingleWeatherFront(L, layer, { ...weather, livingWorld: front });
  } else registerSingleWeatherFront(L, layer, weather);
}

function registerSingleWeatherFront(
  L: typeof import("leaflet"), layer: LayerGroup, weather: LiveMapPayload["weather"]
): void {
  const state = weather.livingWorld;
  if (weather.controllerEvidenceState !== "fresh" || !state?.available || !isRegionalWeatherFront(state.effect) || !state.route) return;
  const route = state.route;
  const start = gatewayWorldToVulnonaMap(route.start.y, route.start.x);
  const finish = gatewayWorldToVulnonaMap(route.finish.y, route.finish.x);
  const style = weatherFrontStyle(state.regional?.preset, state.effect);
  const points: [number, number][] = [[start.lat, start.lng], [finish.lat, finish.lng]];
  L.polyline(points, { interactive: false, color: "#071713", opacity: .8, weight: 8, lineCap: "round" }).addTo(layer);
  L.polyline(points, { interactive: false, className: "living-world-storm-route", color: style.color,
    opacity: .9, weight: 2, dashArray: "5 9", lineCap: "round" }).addTo(layer);
  const angle = Math.atan2(start.lat - finish.lat, finish.lng - start.lng) * 180 / Math.PI;
  for (const progress of [.2, .55, .9]) {
    L.marker([start.lat + (finish.lat - start.lat) * progress, start.lng + (finish.lng - start.lng) * progress], {
      interactive: false, keyboard: false, icon: L.divIcon({ className: "weather-travel-arrow", iconSize: [24, 24], iconAnchor: [12, 12],
        html: '<span style="color:' + style.color + ';transform:rotate(' + angle + 'deg)">➜</span>' })
    }).addTo(layer);
  }
  const position = liveWeatherFrontPosition(weather);
  if (!position) return;
  const radius = route.radiusMetres * 100;
  const current = gatewayWorldToVulnonaMap(position.y, position.x);
  const sw = gatewayWorldToVulnonaMap(position.y + radius, position.x - radius);
  const ne = gatewayWorldToVulnonaMap(position.y - radius, position.x + radius);
  L.imageOverlay('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(weatherRadarSvg(state.regional?.preset, state.effect)),
    [[sw.lat, sw.lng], [ne.lat, ne.lng]], { interactive: false, opacity: .9 * (state.regional?.alpha ?? 1), className: "regional-weather-radar" }).addTo(layer);
  const boundary = Array.from({ length: 64 }, (_, index) => {
    const angle = index * Math.PI * 2 / 64;
    const point = gatewayWorldToVulnonaMap(position.y + Math.sin(angle) * radius, position.x + Math.cos(angle) * radius);
    return [point.lat, point.lng] as [number, number];
  });
  L.polygon(boundary, { interactive: false, color: style.color, fill: false, opacity: .24, weight: 1,
    dashArray: "2 8", className: "living-world-front-boundary" }).addTo(layer);
  const label = weatherName(state.regional?.preset ?? state.effect);
  L.marker(current, { pane: "weatherLabels", interactive: false, keyboard: false, zIndexOffset: 300, icon: L.divIcon({ className: "weather-front-marker", iconSize: [0, 0],
    html: '<div class="weather-front-label" style="--front-color:' + style.color + '"><i>' + style.icon + '</i><span><small>LIVE · ' + escapeHtml(weatherName(state.region)) + '</small><strong>' + escapeHtml(label) + '</strong></span></div><span class="weather-front-dot" style="--front-color:' + style.color + '"></span>' })
  }).addTo(layer);
}

export function liveWeatherFrontPosition(weather: LiveMapPayload["weather"], now = Date.now()): { x: number; y: number; observedAt: number } | undefined {
  const state = weather.livingWorld;
  if (state?.fronts && state.available && weather.controllerEvidenceState === "fresh") {
    for (const front of state.fronts) {
      const position: { x: number; y: number; observedAt: number } | undefined = liveWeatherFrontPosition({ ...weather, livingWorld: front }, now);
      if (position) return position;
    }
    return undefined;
  }
  if (weather.controllerEvidenceState !== "fresh" || !state?.available || !isRegionalWeatherFront(state.effect)
    || !state.route || !["active", "moving", "fading"].includes(state.phase)) return undefined;
  const observedAt = new Date(state.observedAt).getTime();
  if (!Number.isFinite(observedAt) || observedAt > now || now - observedAt > 30_000
    || (state.endsAt && new Date(state.endsAt).getTime() <= now)) return undefined;
  return observedWeatherFrontPosition(state.route, now);
}

function replaceWeatherLayer(L: typeof import("leaflet"), map: LeafletMap, weather: LiveMapPayload["weather"]): void {
  const current = activeLayers.get("weather");
  const toggle = document.querySelector<HTMLInputElement>('[data-map-layer-toggle="weather"]');
  const wasAvailable = toggle?.disabled === false;
  const enabled = wasAvailable ? Boolean(toggle?.checked) : weather.evidenceState === "fresh";
  if (current && map.hasLayer(current)) map.removeLayer(current);
  activeLayers.delete("weather");
  syncWeatherTelemetryDom(weather, enabled);
  registerWeatherLayer(L, map, weather);
  const next = activeLayers.get("weather");
  if (!enabled && next && map.hasLayer(next)) map.removeLayer(next);
  syncWeatherLensVisibility(enabled);
}

function replaceEnvironmentOutlookLayer(L: typeof import("leaflet"), map: LeafletMap, weather: LiveMapPayload["weather"]): void {
  const current = activeLayers.get("outlook");
  const toggle = document.querySelector<HTMLInputElement>('[data-map-layer-toggle="outlook"]');
  const wasAvailable = toggle?.disabled === false;
  const available = Boolean(modelledWeatherOutlookRoute(weather));
  const enabled = wasAvailable ? Boolean(toggle?.checked) : available;
  if (current && map.hasLayer(current)) map.removeLayer(current);
  activeLayers.delete("outlook");
  registerEnvironmentOutlookLayer(L, map, weather);
  const next = activeLayers.get("outlook");
  if (!enabled && next && map.hasLayer(next)) map.removeLayer(next);
  if (toggle) {
    toggle.disabled = !available;
    toggle.checked = available && enabled;
  }
}

function syncWeatherTelemetryDom(weather: LiveMapPayload["weather"], enabled: boolean): void {
  const live = weather.evidenceState === "fresh";
  const toggle = document.querySelector<HTMLInputElement>('[data-map-layer-toggle="weather"]');
  if (toggle) {
    toggle.disabled = !live;
    toggle.checked = live && enabled;
  }
  const observatory = document.querySelector<HTMLElement>(".map-weather-observatory");
  if (observatory) observatory.dataset.weatherState = live ? "live" : "unavailable";
  const clock = document.querySelector<HTMLElement>("[data-weather-game-clock]");
  if (clock) clock.textContent = gameClockLabel(live ? weather.timeOfDay : null);
  const condition = document.querySelector<HTMLElement>("[data-weather-condition]");
  const observed = document.querySelector<HTMLElement>("[data-weather-observed]");
  const controller = document.querySelector<HTMLElement>("[data-weather-controller]");
  if (condition) condition.textContent = regionalWeatherSummary(weather);
  if (observed) observed.textContent = live
    ? `Base sky sampled ${new Date(weather.observedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
    : "No current game-server reading — the map will not estimate conditions.";
  if (controller) controller.textContent = live ? `Base sky: ${humanizeWeatherCondition(weather.condition)}` : "Base sky unavailable";
  const detail = document.querySelector<HTMLElement>("[data-weather-explanation]");
  if (detail) detail.textContent = regionalWeatherDetail(weather);
  const focus = document.querySelector<HTMLButtonElement>("[data-map-weather-focus]");
  if (focus) focus.disabled = !liveWeatherFrontPosition(weather);
  const forecast = document.querySelector<HTMLElement>("[data-weather-forecast]");
  if (forecast) forecast.innerHTML = environmentDirectorForecastHtml(weather);
  const metrics: Record<string, number | null> = {
    cloud: weather.cloudCoverage,
    rain: weather.rainIntensity,
    fog: weather.fogIntensity,
    wind: weather.windIntensity,
    lightning: weather.lightningIntensity
  };
  for (const [name, value] of Object.entries(metrics)) {
    const element = document.querySelector<HTMLElement>(`[data-weather-metric="${name}"]`);
    if (element) element.textContent = live && value !== null ? `${Math.max(0, Math.min(100, Math.round(value)))}%` : "—";
  }
}

function livingWorldSummary(weather: LiveMapPayload["weather"]): string {
  const state = weather.livingWorld;
  if (weather.controllerEvidenceState !== "fresh" || !state?.available) return "Living World controller unavailable";
  if (state.effect === "none") return `Living World dormant · ${humanizeWeatherCondition(state.phase)}`;
  return `${humanizeWeatherCondition(state.effect)} · ${humanizeWeatherCondition(state.phase)} · ${humanizeWeatherCondition(state.region)}`;
}

export function environmentDirectorForecastHtml(weather: LiveMapPayload["weather"]): string {
  const regional = regionalForecastHtml(weather);
  if (regional) return regional;
  return `<span><small>GATEWAY WEATHER / OUTLOOK</small><strong>Waiting for a live weather forecast</strong></span><div><span class="is-empty"><strong>Live observations appear above</strong><small>The forecast returns when the regional controller reports its next windows.</small></span></div>`;
}

function syncWeatherLensVisibility(enabled: boolean): void {
  const container = document.querySelector<HTMLElement>("#liveMapCanvas");
  if (!container) return;
  container.dataset.weatherKind = enabled ? container.dataset.weatherObservedKind ?? "unavailable" : "unavailable";
}

function humanizeWeatherCondition(value: string): string {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function registerHeatmapLayer(L: typeof import("leaflet"), map: LeafletMap, points: HeatmapPoint[]): void {
  const imageUrl = createHeatmapImage(points);
  const layer = imageUrl
    ? L.imageOverlay(imageUrl, gatewayBounds(), {
      className: "map-activity-heatmap",
      interactive: false,
      opacity: 0.9
    })
    : L.layerGroup();
  activeLayers.set("heatmap", layer);
  if (layerEnabled("heatmap", false)) layer.addTo(map);
}

function replaceHeatmapLayer(L: typeof import("leaflet"), map: LeafletMap, points: HeatmapPoint[]): void {
  const previous = activeLayers.get("heatmap");
  if (previous) {
    map.removeLayer(previous);
    activeLayers.delete("heatmap");
  }
  registerHeatmapLayer(L, map, points);
}

function registerPlayerMarkers(L: typeof import("leaflet"), map: LeafletMap, points: LiveMapPoint[]): void {
  const layer = L.layerGroup().addTo(map);
  activePlayerLayer = layer;
  for (const point of points) {
    const mapped = gatewayWorldToVulnonaMap(point.latitude, point.longitude);
    const position: [number, number] = [mapped.lat, mapped.lng];
    if (point.kind === "self") selfPosition = position;
    const marker = L.circleMarker(position, {
      radius: point.kind === "self" ? 7 : 6,
      color: "#07100d",
      weight: 3,
      fillColor: point.kind === "self" ? "#f7d14a" : "#36c98a",
      fillOpacity: 1,
      interactive: false
    }).addTo(layer);
    const label = document.createElement("span");
    label.className = `map-player-tooltip map-player-tooltip-${point.kind}`;
    label.textContent = point.kind === "self" ? `${point.name} (you)` : point.name;
    marker.bindTooltip(label, {
      className: `map-player-label map-player-label-${point.kind}`,
      direction: "top",
      offset: [0, -8],
      opacity: 1,
      permanent: true
    });
  }
}

function replacePlayerMarkers(L: typeof import("leaflet"), map: LeafletMap, points: LiveMapPoint[]): void {
  if (activePlayerLayer) map.removeLayer(activePlayerLayer);
  selfPosition = undefined;
  registerPlayerMarkers(L, map, points);
}

function registerLocationLabels(L: typeof import("leaflet"), map: LeafletMap, layer: LayerGroup): void {
  activeLocationLabels = [];
  const anchor = L.divIcon({ className: "map-location-anchor", html: "", iconSize: [1, 1], iconAnchor: [0, 0] });
  for (const [sourceIndex, location] of RENATUS_LOCATION_LABELS.entries()) {
    if (!isMapPixel(location.x) || !isMapPixel(location.y)) continue;
    const locationPoint = gatewayStoredMapPointToVulnona(location.x, location.y);
    const marker = L.marker([MAP_SIZE - locationPoint.mapY, locationPoint.mapX], {
      icon: anchor,
      interactive: false,
      keyboard: false
    });
    marker.bindTooltip(escapeHtml(location.label), {
      className: `map-location-label map-location-label-${location.group}`,
      direction: locationLabelDirection(locationPoint.mapX, locationPoint.mapY),
      offset: [0, locationPoint.mapY < 520 ? 8 : -8],
      opacity: 1,
      permanent: true
    });
    marker.addTo(layer);
    activeLocationLabels.push({ marker, priority: locationLabelPriority(location.group), sourceIndex });
  }
  scheduleLocationLabelLayout(map);
}

export function locationLabelDirection(x: number, y: number): "right" | "left" | "bottom" | "top" {
  if (x < 520) return "right";
  if (x > MAP_SIZE - 520) return "left";
  if (y < 520) return "bottom";
  return "top";
}

function locationLabelPriority(group: RenatusLocationLabel["group"]): number {
  if (group === "landmark") return 3;
  if (group === "water") return 2;
  return 1;
}

function scheduleLocationLabelLayout(map: LeafletMap): void {
  if (locationLayoutFrame !== undefined) window.cancelAnimationFrame(locationLayoutFrame);
  locationLayoutFrame = window.requestAnimationFrame(() => {
    locationLayoutFrame = undefined;
    layoutLocationLabels(map);
  });
}

function layoutLocationLabels(map: LeafletMap): void {
  const locationLayer = activeLayers.get("locations");
  if (!locationLayer || !map.hasLayer(locationLayer)) return;

  const mapRect = map.getContainer().getBoundingClientRect();
  const occupied = Array.from(map.getContainer().querySelectorAll<HTMLElement>(".map-player-label"))
    .filter((element) => element.offsetParent !== null)
    .map((element) => element.getBoundingClientRect());
  const sortedLabels = [...activeLocationLabels].sort((left, right) =>
    right.priority - left.priority || left.sourceIndex - right.sourceIndex
  );

  for (const { marker } of sortedLabels) {
    const element = marker.getTooltip()?.getElement();
    if (!element) continue;
    element.classList.remove("is-decluttered");
    const rect = element.getBoundingClientRect();
    const insideMap = rect.left >= mapRect.left + 4
      && rect.right <= mapRect.right - 4
      && rect.top >= mapRect.top + 4
      && rect.bottom <= mapRect.bottom - 4;
    const overlaps = occupied.some((visible) => rectanglesOverlap(rect, visible, 5));
    const visible = insideMap && !overlaps;
    element.classList.toggle("is-decluttered", !visible);
    if (visible) occupied.push(rect);
  }
}

function rectanglesOverlap(left: DOMRect, right: DOMRect, gap: number): boolean {
  return left.left < right.right + gap
    && left.right + gap > right.left
    && left.top < right.bottom + gap
    && left.bottom + gap > right.top;
}

function parsePayload(raw: string): LiveMapPayload | undefined {
  try {
    const parsed = JSON.parse(raw) as Partial<LiveMapPayload>;
    if (!Array.isArray(parsed.points) || !Array.isArray(parsed.heatmap) || !parsed.weather || typeof parsed.weather !== "object") return undefined;
    return { points: parsed.points, heatmap: parsed.heatmap, weather: parsed.weather as LiveMapPayload["weather"] };
  } catch {
    return undefined;
  }
}

function gatewayBounds() {
  return [[0, 0], [MAP_SIZE, MAP_SIZE]] as [[number, number], [number, number]];
}

function layerEnabled(name: MapLayerName, defaultValue: boolean): boolean {
  const toggle = document.querySelector<HTMLInputElement>(`[data-map-layer-toggle="${name}"]`);
  return toggle?.checked ?? defaultValue;
}

function isMapLayerName(value: string): value is MapLayerName {
  return ["locations", "sanctuaries", "weather", "outlook", "migration", "patrol", "heatmap"].includes(value);
}

function isMapPixel(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= MAP_SIZE;
}

function createHeatmapImage(points: HeatmapPoint[]): string | undefined {
  if (points.length === 0 || typeof document === "undefined") return undefined;
  const size = 640;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return undefined;
  context.globalCompositeOperation = "lighter";
  const kernelRadius = (560 / MAP_SIZE) * size;
  for (const point of points) {
    const mapped = gatewayWorldToVulnonaMap(point.latitude, point.longitude);
    const x = (mapped.lng / MAP_SIZE) * size;
    const y = ((MAP_SIZE - mapped.lat) / MAP_SIZE) * size;
    const strength = point.density === "high" ? 0.76 : point.density === "medium" ? 0.56 : 0.36;
    const gradient = context.createRadialGradient(x, y, 0, x, y, kernelRadius);
    gradient.addColorStop(0, `rgba(255,255,255,${strength})`);
    gradient.addColorStop(0.24, `rgba(255,255,255,${strength * 0.78})`);
    gradient.addColorStop(0.58, `rgba(255,255,255,${strength * 0.34})`);
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = gradient;
    context.fillRect(x - kernelRadius, y - kernelRadius, kernelRadius * 2, kernelRadius * 2);
  }
  const pixels = context.getImageData(0, 0, size, size);
  for (let index = 0; index < pixels.data.length; index += 4) {
    const intensity = Math.min(1, pixels.data[index + 3]! / 210);
    const [red, green, blue, alpha] = heatmapRasterColor(intensity);
    pixels.data[index] = red;
    pixels.data[index + 1] = green;
    pixels.data[index + 2] = blue;
    pixels.data[index + 3] = alpha;
  }
  context.globalCompositeOperation = "source-over";
  context.putImageData(pixels, 0, 0);
  return canvas.toDataURL("image/png");
}

export function heatmapRasterColor(intensity: number): [number, number, number, number] {
  const value = Math.max(0, Math.min(1, intensity));
  if (value < 0.035) return [0, 0, 0, 0];
  const stops: Array<[number, [number, number, number]]> = [
    [0, [16, 137, 151]],
    [0.32, [35, 205, 82]],
    [0.62, [242, 218, 40]],
    [0.82, [245, 121, 24]],
    [1, [207, 50, 36]]
  ];
  const upperIndex = stops.findIndex(([position]) => position >= value);
  const upper = stops[Math.max(1, upperIndex)]!;
  const lower = stops[Math.max(0, Math.max(1, upperIndex) - 1)]!;
  const mix = upper[0] === lower[0] ? 0 : (value - lower[0]) / (upper[0] - lower[0]);
  const channel = (offset: number) => Math.round(lower[1][offset]! + ((upper[1][offset]! - lower[1][offset]!) * mix));
  return [channel(0), channel(1), channel(2), Math.round(Math.min(0.82, value * 0.94) * 255)];
}

function syncHeatmapLegend(enabled: boolean): void {
  const legend = document.querySelector<HTMLElement>("[data-map-heatmap-legend]");
  if (legend) legend.hidden = !enabled;
}

export function gatewayZoneStyle(kind: GatewayZoneKind): {
  stroke: string;
  fill: string;
  fillOpacity: number;
  opacity: number;
  weight: number;
  dashArray?: string;
} {
  if (kind === "sanctuary") {
    return { stroke: "#50d395", fill: "#2c9d69", fillOpacity: 0.18, opacity: 0.86, weight: 1.4 };
  }
  if (kind === "migration") {
    return { stroke: "#d4ef72", fill: "#769c3d", fillOpacity: 0.08, opacity: 0.98, weight: 1.9 };
  }
  return {
    stroke: "#e7a1ff",
    fill: "#8739a8",
    fillOpacity: 0.16,
    opacity: 1,
    weight: 2.15,
    dashArray: "7 4"
  };
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
}
